import { useState } from 'react'

import { DateRange, OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Upload } from '@factorialco/f0-react/icons/app'
import { endOfMonth, startOfMonth } from 'date-fns'
import {
  SalaryAdvanceSummaryCollectionDocument,
  SalaryAdvanceSummaryCollectionQuery,
  SalaryAdvanceSummaryCompensationFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { buildUrl } from 'modules/core/lib/buildUrl'
import { i18nDateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { ExportSidepanel } from 'modules/universal_exports/components/ExportSidepanel'

export const SummaryTable = () => {
  const { access } = useCurrentSession()
  const locale = access.locale || 'en'

  const [showExportModal, setShowExportModal] = useState(false)
  const [range, setRange] = useState<DateRange>({
    from: startOfMonth(new Date()),
    to: endOfMonth(new Date()),
  })
  const startDate = range.from
  const endDate = range.to ?? endOfMonth(range.from)

  const source = useGraphqlDataCollectionSource(
    {
      document: SalaryAdvanceSummaryCollectionDocument,
      fetchPolicy: 'cache-and-network',
      perPage: 25,
      navigationFilters: {
        date: {
          defaultValue: range.from,
          granularity: 'month',
          type: 'date-navigator',
          max: new Date(),
        },
      },
      mapData: (data: SalaryAdvanceSummaryCollectionQuery) => {
        return data.salaryAdvance.compensationsConnection.edges.map((edge) => edge.node)
      },
      mapPagination: (data: SalaryAdvanceSummaryCollectionQuery) => {
        return {
          total: data.salaryAdvance.compensationsConnection.totalCount ?? 0,
        }
      },

      mapVariables: ({ navigationFilters }) => {
        const dateRange = navigationFilters?.date?.value ?? range

        setRange(dateRange)

        const currentStartDate = dateRange.from
        const currentEndDate = dateRange.to ?? endOfMonth(dateRange.from)
        return {
          createdAfter: currentStartDate.toISOString(),
          createdBefore: currentEndDate.toISOString(),
        }
      },
      primaryActions: () => ({
        label: i18n.t('common.export'),
        onClick: () => setShowExportModal(true),
        icon: Upload,
      }),
      totalItemSummary: () => i18n.t('salary_advance.summary.table.title'),
      itemsWithChildren: (item: SalaryAdvanceSummaryCompensationFragment) =>
        !!item.requests?.length && item.requests.length > 0,
      childrenCount: ({ item }: { item: SalaryAdvanceSummaryCompensationFragment }) =>
        item.requests?.length,
      fetchChildren: async ({ item }: { item: SalaryAdvanceSummaryCompensationFragment }) => {
        // OneDataCollection requires children to have the same type as parents.
        // We transform SalaryAdvanceRequest to SalaryAdvanceSummaryCompensationFragment
        // by mapping the shared fields and providing defaults for the rest. See more: https://green-island-0013d7f03.1.azurestaticapps.net/iframe.html?viewMode=docs&id=components-data-collection-visualizations-table--documentation&globals=#fetchchildren
        const records: SalaryAdvanceSummaryCompensationFragment[] = item.requests.map(
          (request) => ({
            __typename: 'SalaryAdvanceCompensation',
            id: request.id,
            amountCents: request.amountCents,
            currency: request.currency,
            employeeName: request.employeeName,
            createdAt: request.createdAt,
            requests: [],
          })
        )
        return {
          records,
          type: 'basic',
        }
      },
      search: {
        enabled: true,
      },
    },
    [range]
  )

  return (
    <>
      <OneDataCollection
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('common.employee'),
                  render: (item: SalaryAdvanceSummaryCompensationFragment) => {
                    // Detect if this is a child by checking if requests is empty
                    const isChild = item.requests.length === 0

                    if (isChild) {
                      // For children (requests), show createdAt date instead of employeeName
                      return {
                        type: 'text',
                        value: item.createdAt
                          ? i18nDateToString(new Date(item.createdAt), 'ui')
                          : '-',
                      }
                    }

                    // For parents (compensations), show employeeName
                    return {
                      type: 'person',
                      value: {
                        firstName: item.employeeName?.firstName ?? '-',
                        lastName: item.employeeName?.lastName ?? '-',
                        src: buildUrl(item.employeeName?.avatar?.url ?? ''),
                      },
                    }
                  },
                },
                {
                  label: i18n.t('salary_advance.summary.table.columns.number_of_transactions'),
                  render: (item: SalaryAdvanceSummaryCompensationFragment) =>
                    item.requests?.length || '-',
                },
                {
                  label: i18n.t('salary_advance.summary.table.columns.total_amount_requested'),
                  render: (item: SalaryAdvanceSummaryCompensationFragment) => {
                    return MoneyWithCurrency({
                      amount: item.amountCents,
                      currency: item.currency,
                      locale,
                    })
                  },
                },
              ],
            },
          },
        ]}
      />
      {showExportModal && (
        <ExportSidepanel
          resource="salary_advance.compensation"
          overrideFilters={{
            created_at: [startDate.toISOString(), endDate.toISOString()],
          }}
          onClose={() => setShowExportModal(false)}
        />
      )}
    </>
  )
}
